import React from 'react'
import {
  TTableColumn,
  TTableRow
} from 'components_new/molecules/SegmentVirtualizedTable'
import { AiIconTip } from 'components_new/molecules/IconTip'
import { DatasetType, IUnderlyingDataResponse } from 'types/GlobalWidget'

import { Sort } from 'utils/types'
import { isNumberDatasetType, parseDatasetValue } from 'utils/functions'
import {
  sortAlphabeticalAsc,
  sortAlphabeticalDesc,
  sortNumericAsc,
  sortNumericDesc
} from 'utils/sortHelper'
import { TFunction } from 'language/translator'

const SEGMENT_COL = 'segment'

export type ParsedData = {
  columns: TTableColumn[]
  rows: TTableRow[]
}

// Per-column free-text search, column id -> query. Empty/missing = no search.
export type ColumnSearch = { [columnId: string]: string }

/**
 * Retreive and parse columns from Underlying data response.
 * @param raw - API Response from either underlying data or
 *              Preview of entity.
 */
export function getParsedData(
  raw: IUnderlyingDataResponse,
  aiColumnIds: Set<string>,
  t: TFunction
): ParsedData {
  const columns: TTableColumn[] = [
    {
      id: SEGMENT_COL,
      label: raw.labels.label,
      type: DatasetType.STRING,
      hidden: raw.labels.hidden
    },
    ...raw.data.map((col) => {
      const parsedCol = {
        id: col.entity_column_id ?? (col.kpi_option_id as string),
        label: col.unit ? `${col.label} (${col.unit})` : col.label,
        type: col.type
      }

      if (aiColumnIds.has(parsedCol.id)) {
        return {
          ...parsedCol,
          endAdornment: (
            <AiIconTip sx={{ ml: 0.5 }} title={t('global', 'ai_generated')} />
          )
        }
      }

      return parsedCol
    })
  ]

  const rows: TTableRow[] = raw.labels.data.map((_, i) => {
    const row = {
      /*-- segment --*/
      segment: {
        label: raw.labels.data[i].label,
        link: raw.external_links[i]
      },
      values: {}
    } as TTableRow

    raw.data.forEach((col) => {
      if (col.entity_column_id) {
        row.values[col.entity_column_id] = col.data[i] as string[] | number[]
      } else if (col.kpi_option_id) {
        // parse kpi to use max 2 decimals
        const value = col.data[i] as number | null

        row.values[col.kpi_option_id] =
          value !== null ? parseFloat(value.toFixed(2)) : null
      }
    })

    return row
  })

  return { columns, rows }
}

// The value a row sorts on. Read once per row rather than once per
// comparison: sorting 80k rows asks for the same cell some 17 times
// otherwise.
function getSortValue(
  row: TTableRow,
  column: TTableColumn,
  numeric: boolean
): number | string | null {
  if (column.id === SEGMENT_COL) {
    return row.segment.label
  }

  const value = parseDatasetValue(row.values[column.id], column.type)

  if (numeric) {
    return value as number | null
  }

  return ((value as string[] | null) ?? []).join(',')
}

export function sortParsedData(data: ParsedData, sort: Sort): ParsedData {
  const sortingCol = data.columns.find((col) => col.id === sort.attribute)

  if (!sortingCol) {
    return data
  }

  const numeric = Boolean(isNumberDatasetType(sortingCol.type))
  const values = data.rows.map((row) => getSortValue(row, sortingCol, numeric))
  const order = data.rows.map((_, index) => index)

  order.sort((a, b) => {
    if (numeric) {
      const first = values[a] as number | null
      const second = values[b] as number | null

      return sort.direction === 'asc'
        ? sortNumericAsc(first, second)
        : sortNumericDesc(first, second)
    }

    const first = values[a] as string
    const second = values[b] as string

    return sort.direction === 'asc'
      ? sortAlphabeticalAsc(first, second)
      : sortAlphabeticalDesc(first, second)
  })

  return {
    columns: data.columns,
    rows: order.map((index) => data.rows[index])
  }
}

const normalizeSearchText = (value: string) =>
  value.toLowerCase().replace(/\s/g, '')

/**
 * The displayed text of a numeric cell. Shared with the table so a search
 * always matches exactly what is on screen. Values arrive as one element
 * arrays as often as plain numbers, and both localize the same way.
 */
export function formatNumberValue(value: unknown): string | null {
  return (value as number | null)?.toLocaleString('sv-SE') ?? null
}

// Separates the variants of a searchable cell. Not typeable, so a query can
// never match across two of them.
const VARIANT_SEPARATOR = '\u0000'

/**
 * The text a cell is searched against: what the table displays, plus the raw
 * value for numbers so "1234.5" also matches the displayed "1 234,5".
 */
function getCellSearchText(row: TTableRow, column: TTableColumn): string {
  if (column.id === SEGMENT_COL) {
    return row.segment.label
  }

  const value = parseDatasetValue(
    row.values[column.id],
    column.type ?? DatasetType.STRING
  )

  if (isNumberDatasetType(column.type)) {
    const text = formatNumberValue(value)

    // The raw value too, so "1234.5" finds the displayed "1 234,5".
    return text === null ? '' : `${text}${VARIANT_SEPARATOR}${value}`
  }

  return ((value as string[] | null) ?? []).join(',')
}

/**
 * Normalized cell text per column, built the first time a column is searched
 * and kept for as long as that parsed data lives. Without it every keystroke
 * re-reads and re-normalizes every cell of the column.
 */
const searchIndexes = new WeakMap<ParsedData, Map<string, string[]>>()

function getColumnSearchIndex(
  data: ParsedData,
  column: TTableColumn
): string[] {
  let byColumn = searchIndexes.get(data)

  if (!byColumn) {
    byColumn = new Map()
    searchIndexes.set(data, byColumn)
  }

  const cached = byColumn.get(column.id)

  if (cached) {
    return cached
  }

  const index = data.rows.map((row) =>
    normalizeSearchText(getCellSearchText(row, column))
  )

  byColumn.set(column.id, index)

  return index
}

// Keep the rows matching every active column search (case-insensitive
// "contains", whitespace-insensitive).
export function filterParsedData(
  data: ParsedData,
  search: ColumnSearch
): ParsedData {
  const active = Object.entries(search)
    .map(([columnId, query]) => ({
      column: data.columns.find((col) => col.id === columnId),
      query: normalizeSearchText(query)
    }))
    .filter(
      (item): item is { column: TTableColumn; query: string } =>
        Boolean(item.column) && item.query.length > 0
    )

  if (active.length === 0) {
    return data
  }

  const indexes = active.map((item) => ({
    query: item.query,
    values: getColumnSearchIndex(data, item.column)
  }))

  const rows = data.rows.filter((_, index) =>
    indexes.every((item) => item.values[index].includes(item.query))
  )

  return { columns: data.columns, rows }
}
